import { EventObject, Typestate } from 'xstate';
import {
  Application,
  CheckVerificationTokenError,
  CheckVerificationTokenResponse,
  CustomerEligibility,
  SendVerificationTokenError,
  SendVerificationTokenResponse,
  UpdateCustomerErrors,
  VerifyEmailAddressErrors,
} from '../../gql/graphql';
import { BasicInfoContinuedModel, BasicInfoModel } from './ecommMachine.types';
import { CustomerSearchRequestMachineContext, CustomerSearchResponseMachineContext } from './shared/context/customerSearchMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { CustomerSearchResult } from '../../networkRequests/customerSearch/queryCustomersByCellphone';
import type { BasicInfoContinuedSubmittedEvent, Last4SSNSubmittedEvent } from '../../networkRequests/customerSearch/queryCustomersByIDV';
import { CustomerFlowType } from '../../constants/customer-flow-type';
import { ResolverError } from '../../constants/resolver-error';
import { RoleMachineContext } from './shared/context/roleMachineContext';

// Context Types
export interface CustomerLookupMachineContext
  extends RoleMachineContext,
    OrderIdMachineContext,
    OrderMachineContext,
    CustomerSearchRequestMachineContext,
    CustomerSearchResponseMachineContext {
  highestSupportedApplicationMilestone: number;

  didSkipOTP: boolean;

  skipToPage?: string | null;

  // otp request
  sendCodeErrors: SendVerificationTokenError[];
  // otp check
  checkCodeErrors: CheckVerificationTokenError[];

  verifyEmailErrors?: VerifyEmailAddressErrors[];

  isLast4Different: boolean;
  didCustomerChange: boolean;

  // networking errors, can be assigned by many requests
  resolverErrors: ResolverError[]; // TODO: refactor this out

  otbInitialState?: string; // used to start OTB at a different page
  optedIntoSmsMarketing?: boolean;
  selectedOptIntoSmsMarketing?: boolean;
  // the last 4 / SSN+DOB submit that started an IDV customer search, so the search can be replayed on a new order
  customerSearchEvent?: Last4SSNSubmittedEvent | BasicInfoContinuedSubmittedEvent;

  // Metadata query params
  entrypointParam: string;
}

// State Types
export enum CustomerLookupStates {
  // page states
  OTPRequest = 'OTPRequest',
  OTPCheck = 'OTPCheck',

  Loading = 'Loading',
  NewApplication = 'NewApplication',
  OTB = 'OTB',
  Last4SSN = 'Last4SSN',
  // results of customer search with last 4
  FoundInProgressApp = 'FoundInProgressApp',
  StartNewApplication = 'StartNewApplication',
  ExistingApplication = 'ExistingApplication',
  BasicInfo = 'BasicInfo',
  NoAccountFound = 'NoAccountFound',
  BasicsContinued = 'BasicsContinued',
  Decline = 'Decline',
  CustLookupInitialState = 'CustLookupIntialState',
  InternalError = 'InternalError',
  Ineligible = 'Ineligible',
  AccommodationAddressErrors = 'AccommodationAddressErrors',

  // OTP check substates
  CheckCode = 'CheckCode',
  QueryOrder = 'QueryOrder',
  CustomerSearch = 'CustomerSearch',
  CreatingNewOrder = 'CreatingNewOrder',

  LongFormApplication = 'LongFormApplication',

  PhoneNumberChanged = 'PhoneNumberChanged',
}

export interface CustomerLookupMachineStatesSchema {
  [CustomerLookupStates.OTPRequest]: {};
  [CustomerLookupStates.OTPCheck]: {};
  [CustomerLookupStates.CheckCode]: {};
  [CustomerLookupStates.CustomerSearch]: {};
  [CustomerLookupStates.CustomerSearch]: {};
  [CustomerLookupStates.Loading]: {};
  [CustomerLookupStates.NewApplication]: {};
  [CustomerLookupStates.OTB]: {};
  [CustomerLookupStates.Last4SSN]: {};
  [CustomerLookupStates.FoundInProgressApp]: {};
  [CustomerLookupStates.StartNewApplication]: {};
  [CustomerLookupStates.LongFormApplication]: {};
  [CustomerLookupStates.ExistingApplication]: {};
  [CustomerLookupStates.BasicInfo]: {};
  [CustomerLookupStates.NoAccountFound]: {};
  [CustomerLookupStates.BasicsContinued]: {};
  [CustomerLookupStates.Decline]: {};
  [CustomerLookupStates.InternalError]: {};
  [CustomerLookupStates.Ineligible]: {};
  [CustomerLookupStates.LongFormApplication]: {};
  [CustomerLookupStates.Ineligible]: {};
  [CustomerLookupStates.AccommodationAddressErrors]: {};
  [CustomerLookupStates.PhoneNumberChanged]: {};
  [CustomerLookupStates.CreatingNewOrder]: {};
}

export interface CustomerLookupMachineStates extends Typestate<CustomerLookupMachineContext> {
  type: CustomerLookupMachineStatesSchema;
}

// Event Types
export enum CustomerLookupEvents {
  NEXT = 'NEXT',
  OTP_NEXT = 'OTPNext',
  BASIC_INFO = 'BasicInfo',
  RESEND = 'Resend',
  CUSTOMER_FOUND = 'CustomerFound',
  LAST4SSN = 'Last4SSN',
  OTB = 'OTB',
  DECLINE = 'Decline',
  CUSTOMER_NOTFOUND = 'CustomerNotFound',
  NOT_RECOGNIZED = 'NotRecognized',
  VALIDATION_ERROR = 'ValidationError',
  BACK = 'BACK',
  NEW_APPLICATION = 'NewApplication',
  OTB_APPLY = 'OTB_APPLY',
  CHECK_OTP = 'CHECK_OTP',
  // OTB_APPLICATION = 'OTB_APPLICATION',
  SEND_TOKEN = 'SEND_TOKEN',
  RECEIVED_TOKEN = 'RECEIVED_TOKEN',
  SEND_TOKEN_ERR = 'SEND_TOKEN_ERR',
  SUBMIT_BASICS_CONTINUED = 'SUBMIT_BASICS_CONTINUED',
  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',
  SKIP_OTP = 'SKIP_OTP',
  RESTART_APPLICATION = 'RESTART_APPLICATION',
}

type CustomerLookupEventsTypeSchema =
  | CustomerLookupEvents.NEW_APPLICATION
  | CustomerLookupEvents.NEXT
  | CustomerLookupEvents.OTP_NEXT
  | CustomerLookupEvents.BASIC_INFO
  | CustomerLookupEvents.RESEND
  | CustomerLookupEvents.CUSTOMER_FOUND
  | CustomerLookupEvents.LAST4SSN
  | CustomerLookupEvents.OTB
  | CustomerLookupEvents.DECLINE
  | CustomerLookupEvents.CUSTOMER_NOTFOUND
  | CustomerLookupEvents.NOT_RECOGNIZED
  | CustomerLookupEvents.VALIDATION_ERROR
  | CustomerLookupEvents.BACK
  | CustomerLookupEvents.CHECK_OTP
  // | CustomerLookupEvents.OTB_APPLICATION
  | CustomerLookupEvents.SEND_TOKEN
  | CustomerLookupEvents.RECEIVED_TOKEN
  | CustomerLookupEvents.OTB_APPLY
  | CustomerLookupEvents.SEND_TOKEN_ERR
  | CustomerLookupEvents.SUBMIT_BASICS_CONTINUED
  | CustomerLookupEvents.TRACK
  | CustomerLookupEvents.PAGE
  | CustomerLookupEvents.IDENTIFY
  | CustomerLookupEvents.SKIP_OTP
  | CustomerLookupEvents.RESTART_APPLICATION;

type CustomerLookupEventsData = {
  customerFlowType?: CustomerFlowType;
  isCurrentApplyTypeOTB?: boolean;
  highestSupportedApplicationMilestone?: number;
  eligibilityDetails?: CustomerEligibility;
  // order?: Order;
  cellPhone: string;
  basicInfoContinued: BasicInfoContinuedModel;
  didCustomerChange: boolean;
  updatedApplication: Application;
  updateBasicInfoContinuedData: {};
  updateBasicInfoContinuedErrors: UpdateCustomerErrors[];
  taxIdLast4: string;
  selectedOptIntoSmsMarketing?: boolean;
} & Omit<SendVerificationTokenResponse, '__typename'> &
  Omit<CheckVerificationTokenResponse, '__typename'> &
  Omit<BasicInfoModel, '__typename'> &
  Omit<CustomerSearchResult, '__typename'>;

export interface CustomerLookupMachineEvents extends EventObject {
  type: CustomerLookupEventsTypeSchema;
  data?: CustomerLookupEventsData;
}

// Guard Types
export enum CustomerLookupGuardsType {
  EXISTING_CUSTOMER_FOUND = 'ExistingCustomerFound',
  CUSTOMER_OTB = 'CustomerOTB',
  CUSTOMER_DECLINED = 'CustomerDeclined',
  CUSTOMER_NOT_FOUND = 'CustomerNotFound',
  SKIP_TO_PAGE = 'SkipToPage',
  NORMAL_INITIAL = 'NormalInitial',
}

type CustomerLookupGuardsTypeSchema =
  | CustomerLookupGuardsType.EXISTING_CUSTOMER_FOUND
  | CustomerLookupGuardsType.CUSTOMER_OTB
  | CustomerLookupGuardsType.CUSTOMER_NOT_FOUND
  | CustomerLookupGuardsType.CUSTOMER_DECLINED
  | CustomerLookupGuardsType.NORMAL_INITIAL
  | CustomerLookupGuardsType.SKIP_TO_PAGE;

export interface CustomerLookupMachineGuards extends EventObject {
  type: CustomerLookupGuardsTypeSchema;
}

export const STARTING_PAGE_OTP_BYPASS = 'SkipOtp';
